import { makeStyles, shorthands } from "@fluentui/react-components";
import { BRAND } from "./tokens";
import { sectionColorHex } from "./sectionColor";
import {
  groupTriggeredDetailAnalysesByBereich,
  type DetailAnalysisDef,
} from "./detailAnalyses";
import type { Answers } from "./types";

const BEREICH_TITLES: Record<1 | 2 | 3 | 4, string> = {
  1: "Bereich 1 — Verlust / Beschädigung / Zerstörung von Sachwerten",
  2: "Bereich 2 — Haftung / Recht",
  3: "Bereich 3 — Menschen im Unternehmen",
  4: "Bereich 4 — Liquidität / Ertragsausfall",
};

const useStyles = makeStyles({
  intro: {
    marginTop: 0,
    marginBottom: "12px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.muted,
  },
  bereich: {
    marginBottom: "20px",
  },
  bereichHead: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "12px",
    color: BRAND.white,
    ...shorthands.borderRadius("6px"),
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    fontWeight: 400,
    marginBottom: "8px",
  },
  bereichEmpty: {
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "12px",
    fontSize: "0.85rem",
    color: BRAND.muted,
    backgroundColor: BRAND.cardBg,
    ...shorthands.border("1px", "dashed", BRAND.border),
    ...shorthands.borderRadius("6px"),
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "10px",
    "& > *": { minWidth: 0 },
    "@media (min-width: 768px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  card: {
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    backgroundColor: BRAND.cardBg,
    ...shorthands.borderRadius("6px"),
    ...shorthands.borderLeft("4px", "solid", BRAND.gold),
    ...shorthands.border("1px", "solid", BRAND.border),
  },
  cardTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "8px",
    marginBottom: "4px",
  },
  cardTitle: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    fontWeight: 700,
    color: BRAND.ink,
  },
  pillAlways: {
    display: "inline-block",
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "8px",
    paddingRight: "8px",
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    backgroundColor: "rgba(247,168,35,0.18)",
    color: "#7a4c00",
    ...shorthands.borderRadius("999px"),
  },
  pillConditional: {
    display: "inline-block",
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "8px",
    paddingRight: "8px",
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    backgroundColor: "rgba(45,20,43,0.08)",
    color: BRAND.muted,
    ...shorthands.borderRadius("999px"),
  },
  desc: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.muted,
  },
  noneBox: {
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "16px",
    paddingRight: "16px",
    backgroundColor: BRAND.cardBg,
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("8px"),
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.muted,
  },
});

const Card = ({ def }: { def: DetailAnalysisDef }) => {
  const styles = useStyles();
  const accent = sectionColorHex[def.color];
  return (
    <div className={styles.card} style={{ borderLeftColor: accent }}>
      <div className={styles.cardTopRow}>
        <span className={styles.cardTitle}>{def.title}</span>
        {def.certainty === "always" ? (
          <span className={styles.pillAlways}>Auf jeden Fall</span>
        ) : (
          <span className={styles.pillConditional}>Eventuell</span>
        )}
      </div>
      <div className={styles.desc}>{def.description}</div>
    </div>
  );
};

type Props = {
  answers: Answers;
};

const DetailAnalysesView = ({ answers }: Props) => {
  const styles = useStyles();
  const grouped = groupTriggeredDetailAnalysesByBereich(answers);
  const total =
    grouped[1].length + grouped[2].length + grouped[3].length + grouped[4].length;

  if (total === 0) {
    return (
      <div className={styles.noneBox}>
        Auf Basis Ihrer aktuellen Antworten sind keine vertiefenden Detailanalysen
        notwendig. Sollte sich daran nach dem Absenden etwas ändern, kommen wir auf Sie zu.
      </div>
    );
  }

  return (
    <div>
      <p className={styles.intro}>
        Aus Ihren Angaben ergeben sich {total} empfohlene Detailanalysen — gruppiert nach
        Risikobereich. Die Detailanalysen führen wir gemeinsam mit Ihnen im Anschluss durch.
      </p>
      {([1, 2, 3, 4] as const).map((bereich) => {
        const items = grouped[bereich];
        const color =
          bereich === 1
            ? sectionColorHex.pillarOrange
            : bereich === 2
              ? sectionColorHex.blue
              : bereich === 3
                ? sectionColorHex.green
                : sectionColorHex.black;
        return (
          <div key={bereich} className={styles.bereich}>
            <div className={styles.bereichHead} style={{ backgroundColor: color }}>
              {BEREICH_TITLES[bereich]}
              {items.length > 0 && (
                <span style={{ marginLeft: "auto", fontSize: "0.8rem" }}>
                  {items.length} {items.length === 1 ? "Analyse" : "Analysen"}
                </span>
              )}
            </div>
            {items.length === 0 ? (
              <div className={styles.bereichEmpty}>
                Keine vertiefenden Analysen erforderlich.
              </div>
            ) : (
              <div className={styles.cardGrid}>
                {items.map((d) => (
                  <Card key={d.key} def={d} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DetailAnalysesView;
